import LabelWrapper from "@/components/root/LabelWrapper";
import { RemoveCircleTwoTone, AddCircleTwoTone, Delete, Psychology, AutoFixHigh } from "@mui/icons-material";
import TimePicker from "react-time-picker";
import { Box, IconButton, ToggleButtonGroup, ToggleButton, TextField, CircularProgress } from "@mui/material";
import { TruckInfo } from "../Project";
import { Fragment, useCallback, useState } from "react";
import SpecialTimePicker from "./SpecialTimePicker";

const THERMO_TYPES = ["XX", "3A", "4A", "SA", "3A4A"];
const BORDMATIK_TYPES = ["XX", "3A", "4A", "3A4A", "2A"];
const STAHLMULDE_TYPES = ["XX", "3A", "4A", "SA", "3A4A", "2A"];
const ALLRAD_TYPES = ["XX", "SA"];

export default function TruckInformationRow({
  ti,
  setPositionField,
  deletePosition,
  i,
  assignedDurations,
  durationsLoading,
  routeInfo,
  mainTakt,
}: {
  ti: TruckInfo;
  setPositionField: (i: number, key: keyof TruckInfo | Partial<TruckInfo>, value?: any) => void;
  mainTakt: number;
  deletePosition: (i: number) => void;
  i: number;
  assignedDurations: null | ["HOURS" | "ROUNDS", number];
  durationsLoading: boolean;
}) {
  const specialSettingsUsed =
    !!ti.specialFirstUnload ||
    !!ti.specialDurationUnit ||
    !!ti.specialDuration ||
    (!!ti.specialComment && ti.specialComment.length > 0) ||
    ti.specialTakt === 0 ||
    !!ti.specialTakt;
  const taktState = ti.specialTakt ?? mainTakt;
  const checkMainTakt = useCallback((t: number) => (t === mainTakt ? null : t), [mainTakt]);

  const [specialSettingsOpen, setSpecialSettingsOpen] = useState(specialSettingsUsed);

  return (
    <div key={i} className={"lkwinfobox " + (specialSettingsUsed ? " withSpecialSettings" : "")}>
      <div className="lkwinfobox_number_wrapper">
        <div className="lkwinfobox_number">{i + 1}</div>
      </div>
      <div className="lkwinfobox_rows">
        <div className="lkwinfobox_row">
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              disabled={!ti.amount || ti.amount <= 1}
              onClick={() => setPositionField(i, "amount", !ti.amount ? null : ti.amount - 1 || null)}
            >
              <RemoveCircleTwoTone />
            </IconButton>
            <Box sx={{ mx: "10px" }}>{!ti.amount ? "egal" : ti.amount + "\xa0LKW"}</Box>
            <IconButton onClick={() => setPositionField(i, "amount", !ti.amount ? 1 : ti.amount + 1)}>
              <AddCircleTwoTone />
            </IconButton>
          </Box>
          <div className="lkwtype_wrapper">
            <ToggleButtonGroup
              color="primary"
              orientation="horizontal"
              exclusive
              size="small"
              value={ti.type}
              onChange={(_, truckType) => {
                if (truckType && truckType !== ti.type) {
                  setPositionField(i, {
                    type: truckType,
                    thermo: THERMO_TYPES.includes(truckType) ? !!ti.thermo : false,
                    bordmatik: BORDMATIK_TYPES.includes(truckType) ? !!ti.bordmatik : false,
                    stahlmulde: STAHLMULDE_TYPES.includes(truckType) ? !!ti.stahlmulde : false,
                    allrad: ALLRAD_TYPES.includes(truckType) ? !!ti.allrad : false,
                  });
                }
              }}
            >
              {ti.type === "XX" ? <ToggleButton value="XX">egal</ToggleButton> : null}
              <ToggleButton value="SA">Sattel</ToggleButton>
              <ToggleButton value="3A">3&#8209;Achser</ToggleButton>
              <ToggleButton value="4A">4&#8209;Achser</ToggleButton>
              <ToggleButton value="3A4A">Solo</ToggleButton>
              <ToggleButton value="2A">2&#8209;Achser</ToggleButton>
              <ToggleButton value="HZ">Hängerzug</ToggleButton>
              <ToggleButton value="TL">Tieflader</ToggleButton>
              <ToggleButton value="KR">Kran</ToggleButton>
            </ToggleButtonGroup>

            <ToggleButtonGroup
              color="primary"
              size="small"
              orientation="horizontal"
              value={["thermo", "bordmatik", "stahlmulde", "allrad", "siteWork"].filter((x) => x in ti && !!ti[x])}
              onChange={(_, selections) =>
                setPositionField(
                  i,
                  Object.fromEntries(["thermo", "bordmatik", "stahlmulde", "allrad", "siteWork"].map((x) => [x, selections.includes(x)])),
                )
              }
            >
              {THERMO_TYPES.includes(ti.type) ? <ToggleButton value="thermo">Thermo</ToggleButton> : null}
              {BORDMATIK_TYPES.includes(ti.type) ? <ToggleButton value="bordmatik">Bordmatik</ToggleButton> : null}
              {STAHLMULDE_TYPES.includes(ti.type) ? <ToggleButton value="stahlmulde">Stahlmulde</ToggleButton> : null}
              {ALLRAD_TYPES.includes(ti.type) ? <ToggleButton value="allrad">Allrad</ToggleButton> : null}
              <ToggleButton value="siteWork">+&nbsp;Bst.dienst</ToggleButton>
            </ToggleButtonGroup>
          </div>
          <IconButton color={specialSettingsUsed ? "error" : undefined} onClick={() => setSpecialSettingsOpen((x) => !x)}>
            <Psychology />
          </IconButton>

          <IconButton onClick={() => deletePosition(i)}>
            <Delete />
          </IconButton>
        </div>
        <div style={{ display: specialSettingsOpen ? "flex" : "none" }} className="lkwinfobox_row specialSettings">
          <LabelWrapper label="Erster LKW" sx={{ overflow: "hidden" }}>
            <SpecialTimePicker
              imTakt
              routeInfo={routeInfo}
              value={ti.specialFirstUnload}
              onChange={(specialFirstUnload) =>
                setPositionField(i, {
                  specialFirstUnload,
                })
              }
            />
          </LabelWrapper>
          <LabelWrapper label="Takt" sx={{ flex: "0 !important", minWidth: "auto" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton disabled={taktState < 1} onClick={() => setPositionField(i, "specialTakt", checkMainTakt(taktState - 5))}>
                <RemoveCircleTwoTone />
              </IconButton>
              <Box
                sx={{
                  mx: "10px",
                  minWidth: "65px",
                  textAlign: "center",
                }}
              >
                {taktState > 0 ? taktState + " min" : "kein Takt"}
              </Box>
              <IconButton onClick={() => setPositionField(i, "specialTakt", checkMainTakt(taktState + 5))}>
                <AddCircleTwoTone />
              </IconButton>
            </Box>
          </LabelWrapper>

          <LabelWrapper label="LKW-Einsatzdauer" sx={{ borderLeft: "1px solid #bbb", borderRight: "1px solid #bbb" }}>
            {!ti.specialDurationUnit && durationsLoading ? (
              <CircularProgress />
            ) : (
              <Fragment>
                <ToggleButtonGroup
                  orientation="horizontal"
                  exclusive
                  sx={{ mb: 1 }}
                  size="small"
                  value={ti.specialDurationUnit || (!!assignedDurations ? assignedDurations[0] : "ROUNDS")}
                  onChange={(_, specialDurationUnit) =>
                    setPositionField(i, {
                      specialDuration: undefined,
                      specialDurationUnit,
                      specialDurationValue: ti.specialDurationValue || (!!assignedDurations ? assignedDurations[1] : 1),
                    })
                  }
                >
                  <ToggleButton value={"ROUNDS"}>Runden</ToggleButton>
                  <ToggleButton value={"HOURS"}>Stunden</ToggleButton>
                </ToggleButtonGroup>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <IconButton
                    disabled={(ti.specialDurationValue || (!!assignedDurations ? assignedDurations[1] : 1)) <= 1}
                    onClick={() =>
                      setPositionField(i, {
                        specialDuration: undefined,
                        specialDurationUnit: ti.specialDurationUnit || (!!assignedDurations ? assignedDurations[0] : "ROUNDS"),
                        specialDurationValue: (ti.specialDurationValue || (!!assignedDurations ? assignedDurations[1] : 1)) - 1,
                      })
                    }
                  >
                    <RemoveCircleTwoTone />
                  </IconButton>
                  <Box sx={{ mx: "10px" }}>{ti.specialDurationValue || (!!assignedDurations ? assignedDurations[1] : 1)}</Box>
                  <IconButton
                    onClick={() =>
                      setPositionField(i, {
                        specialDuration: undefined,
                        specialDurationUnit: ti.specialDurationUnit || (!!assignedDurations ? assignedDurations[0] : "ROUNDS"),
                        specialDurationValue: (ti.specialDurationValue || (!!assignedDurations ? assignedDurations[1] : 1)) + 1,
                      })
                    }
                  >
                    <AddCircleTwoTone />
                  </IconButton>
                  {!!ti.specialDurationUnit || !!ti.specialDuration ? (
                    <IconButton
                      onClick={() =>
                        setPositionField(i, {
                          specialDuration: undefined,
                          specialDurationUnit: undefined,
                          specialDurationValue: undefined,
                        })
                      }
                    >
                      <AutoFixHigh />
                    </IconButton>
                  ) : null}
                </Box>
              </Fragment>
            )}
          </LabelWrapper>

          <LabelWrapper sx={{ flex: 1, minWidth: "250px" }} label="Weitere Infos">
            <TextField
              size="small"
              fullWidth
              value={ti.specialComment}
              placeholder="Weitere Infos"
              multiline
              onChange={(e) =>
                setPositionField(i, {
                  specialComment: (e.target as HTMLInputElement).value,
                })
              }
            />
          </LabelWrapper>
        </div>
      </div>
    </div>
  );
}
